import {
  Directive,
  ElementRef,
  HostListener,
  input,
  OnDestroy,
} from '@angular/core';

export type UiTooltipPosition = 'top' | 'bottom' | 'left' | 'right';

const OFFSET = 4;       // px entre el host y el tooltip
const ARROW = 6;        // altura de la flecha
const DELAY_SHOW = 200; // ms antes de mostrar
const DELAY_HIDE = 100; // ms antes de ocultar

/**
 * Directiva: tooltip accesible posicionado con `position: fixed`.
 * Funciona en cualquier elemento del DOM.
 *
 * Uso:
 *   <button [uiTooltip]="'Guardar'" uiTooltipPosition="top">...</button>
 *   <ui-input [uiTooltip]="'Formato: DD/MM/YYYY'" uiTooltipPosition="bottom" />
 */
@Directive({
  selector: '[uiTooltip]',
  standalone: true,
  host: {
    '[attr.aria-describedby]': 'tooltipId',
  },
})
export class UiTooltipDirective implements OnDestroy {
  readonly uiTooltip = input<string>('');
  readonly uiTooltipPosition = input<UiTooltipPosition>('top');
  readonly uiTooltipDisabled = input(false);

  protected readonly tooltipId = `ui-tooltip-${Math.random().toString(36).slice(2, 8)}`;

  private el: HTMLDivElement | null = null;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  @HostListener('mouseenter')
  @HostListener('focusin')
  onShow(): void {
    if (this.uiTooltipDisabled() || !this.uiTooltip()) return;
    this.clearTimers();
    this.showTimer = setTimeout(() => this.create(), DELAY_SHOW);
  }

  @HostListener('mouseleave')
  @HostListener('focusout')
  onHide(): void {
    this.clearTimers();
    this.hideTimer = setTimeout(() => this.destroy(), DELAY_HIDE);
  }

  @HostListener('keydown.escape')
  onEsc(): void {
    this.clearTimers();
    this.destroy();
  }

  ngOnDestroy(): void {
    this.clearTimers();
    this.destroy();
  }

  private create(): void {
    if (this.el) return;

    const tooltip = document.createElement('div');
    tooltip.id = this.tooltipId;
    tooltip.className = 'ui-tooltip';
    tooltip.setAttribute('role', 'tooltip');
    tooltip.textContent = this.uiTooltip();

    // Flecha
    const arrow = document.createElement('span');
    arrow.className = `ui-tooltip__arrow ui-tooltip__arrow--${this.uiTooltipPosition()}`;
    tooltip.appendChild(arrow);

    document.body.appendChild(tooltip);
    this.el = tooltip;

    this.position();

    // Forzar reflow para que la transición se active
    tooltip.getBoundingClientRect();
    tooltip.classList.add('ui-tooltip--visible');
  }

  private position(): void {
    if (!this.el) return;

    const pos = this.uiTooltipPosition();
    const hostRect = this.host.nativeElement.getBoundingClientRect();
    const tipRect = this.el.getBoundingClientRect();

    const gap = OFFSET + ARROW;
    let top = 0;
    let left = 0;

    switch (pos) {
      case 'top':
        top = hostRect.top - tipRect.height - gap;
        left = hostRect.left + hostRect.width / 2 - tipRect.width / 2;
        break;
      case 'bottom':
        top = hostRect.bottom + gap;
        left = hostRect.left + hostRect.width / 2 - tipRect.width / 2;
        break;
      case 'left':
        top = hostRect.top + hostRect.height / 2 - tipRect.height / 2;
        left = hostRect.left - tipRect.width - gap;
        break;
      case 'right':
        top = hostRect.top + hostRect.height / 2 - tipRect.height / 2;
        left = hostRect.right + gap;
        break;
    }

    // Evitar que se salga del viewport horizontalmente
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    left = Math.max(8, Math.min(left, vw - tipRect.width - 8));
    top  = Math.max(8, Math.min(top,  vh - tipRect.height - 8));

    this.el.style.top  = `${top}px`;
    this.el.style.left = `${left}px`;
  }

  private destroy(): void {
    if (!this.el) return;
    this.el.classList.remove('ui-tooltip--visible');
    const el = this.el;
    this.el = null;
    // Esperar a que termine la transición de salida antes de remover del DOM
    setTimeout(() => el.remove(), 150);
  }

  private clearTimers(): void {
    if (this.showTimer !== null) { clearTimeout(this.showTimer); this.showTimer = null; }
    if (this.hideTimer !== null) { clearTimeout(this.hideTimer); this.hideTimer = null; }
  }
}
